import { Children, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { useClickOutside } from 'hooks/click-outside';
import { useCombinedRefs } from 'hooks/combined-refs';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChangeEvent } from 'utils/form-utils';

import styles from './Select.module.scss';

const SelectContext = createContext(null);

const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context)
    throw new Error(`Select compound components cannot be rendered outside the Select component`);
  return context;
};

export const Select = ({
  name,
  value,
  placeholder = '',
  defaultValue,
  disabledReason,
  useDefault = false,
  useSearch = false,
  validators = {},
  register = (...args) => ({ ref: () => {} }),
  setValue,
  disabled,
  children,
  className,
  inputProps = {},
  onChange,
  error,
  noErrorMessage,
  rounded = false,
  small = false,
}) => {
  const { ref, ...registerProps } = register(name, validators);
  const inputRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, inputRef);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const wrapperRef = useRef(null);

  useClickOutside(wrapperRef, () => setShowMenu(false));

  const [options, setOptions] = useState([]);
  const [optionLabel, setOptionLabel] = useState();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [focusedValue, setFocusedValue] = useState(null);
  const [announcement, setAnnouncement] = useState('');

  // Store options
  useEffect(() => {
    setOptions(
      Children.toArray(children).map(child => ({
        label: child.props.children,
        value: child.props.value,
      })),
    );
  }, [children]);

  // Set label
  useEffect(() => {
    if (options.length < 1) setOptionLabel('');
    const option = options.find(option => option.value == value);
    setOptionLabel(option?.label);
  }, [value, options]);

  // Handle default value / first option
  useEffect(() => {
    if (!options.length) return;

    const currentValue = value;

    if (defaultValue && !currentValue) {
      if (setValue && name) {
        setValue(name, defaultValue, { shouldValidate: true, shouldTouch: true });
      } else {
        setOption(defaultValue);
      }
      return;
    }

    if (useDefault && !currentValue && options.length > 0) {
      if (options[0].value === currentValue) return;
      if (setValue && name) {
        setValue(name, options[0].value, { shouldValidate: true, shouldTouch: true });
      } else {
        setOption(options[0].value);
      }
    }
  }, [options, value, defaultValue, useDefault, name, setValue]);

  const setOption = selectedValue => {
    setShowMenu(false);
    setFocusedIndex(-1);
    setFocusedValue(null);

    const selectedOption = options.find(option => option.value == selectedValue);
    if (selectedOption) {
      setAnnouncement(`Selected ${selectedOption.label}`);
    }

    const finalOnChange = onChange || inputProps?.onChange || registerProps?.onChange;
    if (finalOnChange) {
      finalOnChange(ChangeEvent(name, selectedValue));
    }
  };

  const contextValue = useMemo(
    () => ({ setOption, searchQuery, focusedIndex, setFocusedIndex, focusedValue }),
    [setOption, searchQuery, focusedIndex, focusedValue],
  );

  const handleQueryChange = e => setSearchQuery(e.target.value.toLowerCase());

  const visibleOptions = useMemo(() => {
    return Children.toArray(children).filter(child => {
      if (!child) return false;
      return child.props.children.toString().toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [children, searchQuery]);

  // Helpers for open/close
  const openMenu = () => setShowMenu(true);
  const closeMenu = () => setShowMenu(false);

  // Keyboard navigation
  const handleKeyDown = e => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!showMenu) return openMenu();
        setFocusedIndex(prev => {
          const next = (prev + 1) % visibleOptions.length;
          setFocusedValue(visibleOptions[next].props.value);
          return next;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (!showMenu) return openMenu();
        setFocusedIndex(prev => {
          const next = (prev - 1 + visibleOptions.length) % visibleOptions.length;
          setFocusedValue(visibleOptions[next].props.value);
          return next;
        });
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (showMenu && focusedValue) setOption(focusedValue);
        else openMenu();
        break;

      case 'Escape':
      case 'Tab':
        closeMenu();
        break;
    }
  };

  // Focus management - only focus when user interacts, not on mount
  useEffect(() => {
    if (showMenu) menuRef.current?.focus();
  }, [showMenu]);

  // Announce changes to screen readers
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => setAnnouncement(''), 1000);
      return () => clearTimeout(timer);
    }
  }, [announcement]);

  return (
    <SelectContext.Provider value={contextValue}>
      <div ref={wrapperRef} className={cn(styles.root, { [className]: className })}>
        <input
          name={name}
          ref={combinedRef}
          value={value || ''}
          onChange={() => {}}
          {...registerProps}
          {...inputProps}
        />
        <button
          ref={buttonRef}
          type="button"
          className={cn(styles.button, {
            [styles.disabled]: disabled,
            [styles.rounded]: rounded,
            [styles.small]: small,
          })}
          onClick={() => !disabled && setShowMenu(!showMenu)}
          onKeyDown={handleKeyDown}
          aria-expanded={showMenu}
          aria-haspopup="listbox"
          aria-labelledby={`${name}-label`}
          aria-describedby={error ? `${name}-error` : undefined}
          disabled={disabled}
          tabIndex={0}>
          <span className="u-text-ellipsis">
            {optionLabel == null ? (
              <span className={styles.placeholder}>{placeholder}</span>
            ) : (
              optionLabel
            )}
          </span>
        </button>
        <div
          ref={menuRef}
          className={cn(styles.menu, { [styles.menuVisible]: showMenu })}
          role="listbox"
          aria-labelledby={`${name}-label`}
          aria-activedescendant={focusedValue ? `${focusedValue}-option` : undefined}
          style={{ display: showMenu ? 'block' : 'none' }}
          onKeyDown={handleKeyDown}
          tabIndex={-1}>
          {useSearch && (
            <div className={cn(styles.search)}>
              <FeatherIcon className={styles.icon} icon="search" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleQueryChange}
                className={styles.input}
                placeholder="Search..."
                aria-label="Search options"
              />
            </div>
          )}
          <ul role="presentation">
            {Children.map(children, (child, index) =>
              child ? { ...child, props: { ...child.props, index } } : child,
            )}
          </ul>
        </div>
        {error && !noErrorMessage && (
          <span id={`${name}-error`} className={styles.error} role="alert">
            {error}
          </span>
        )}
        {announcement && (
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </div>
        )}
      </div>
    </SelectContext.Provider>
  );
};

Select.propTypes = {
  name: PropTypes.string,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.any,
  useDefault: PropTypes.bool,
  useSearch: PropTypes.bool,
  validators: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  inputProps: PropTypes.object,
  error: PropTypes.string,
  noErrorMessage: PropTypes.bool,
};

const Item = ({ value, children, className, description, index }) => {
  const { setOption, searchQuery, focusedValue } = useSelectContext();
  const itemRef = useRef(null);

  const [filtered, setFiltered] = useState(false);
  const isFocused = focusedValue === value;

  const handleSelected = () => setOption(value);

  useEffect(() => {
    setFiltered(children.toString().toLowerCase().indexOf(searchQuery) < 0);
  }, [searchQuery]);

  useEffect(() => {
    if (isFocused && itemRef.current && !filtered) {
      itemRef.current.focus();
    }
  }, [isFocused, filtered]);

  return (
    <li
      ref={itemRef}
      role="option"
      id={`${value}-option`}
      className={cn(styles.item, {
        [styles.itemHidden]: filtered,
        [className]: className,
        [styles.focused]: isFocused && !filtered,
      })}
      onClick={handleSelected}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelected();
        }
      }}
      tabIndex={-1}
      aria-selected={isFocused && !filtered}>
      {children}
      {description && <p className={styles.itemDescription}>{description}</p>}
    </li>
  );
};

Item.propTypes = {
  value: PropTypes.any,
  children: PropTypes.node,
  className: PropTypes.string,
  description: PropTypes.string,
  index: PropTypes.number,
};

Select.Item = Item;
